import { validateStandardSchema } from '@angular/forms/signals';

import type { LegacyLogo, LogoEntity, LogoStylesLegacy, SelectedFileLegacy } from '@sites/entity-core';
import { logoLegacyDefaultV0Schema } from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { composeForm } from '../../core/compose-form';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type LogoFormModel = LogoEntity;
export type LogoDataModel = LogoEntity['data'];

/**
 * Signal-form view of the logo widget — a trimmed image: one picked file in
 * `data` plus the size cap in the envelope's typed `styles` bag. **Single
 * placement, single factory** (the logo only ever sits in a specific section),
 * so there is no dispatcher and no per-variant assembler pair.
 *
 * No visibility, animation, shadow or position fields exist on this entity — the
 * host's type and model have them commented out and they were never persisted —
 * so the envelope plugin is the only one installed.
 */
export function createLogoForm(options?: BaseLegacyEntityFormOptions<LegacyLogo>) {
    const base = createBaseLegacyEntityForm<LegacyLogo>((schemaPath) => {
        validateStandardSchema(schemaPath, logoLegacyDefaultV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<LogoEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        {
            // --- Picked file ---
            get image() {
                return _raw.data.image;
            },
            setImage: (value: SelectedFileLegacy | null) => {
                _raw.data.image().value.set(value);
            },
            /** Immutable patch over the current picked file (no-op when none is set). */
            updateImage: (patch: Partial<SelectedFileLegacy>) => {
                _raw.data.image().value.update((current) => (current ? { ...current, ...patch } : current));
            },

            // --- Size (envelope-level `styles`, not `data`) ---
            /** Caps the logo box, in px; seeded from the picked file's natural width. */
            get maxWidth() {
                return _raw.styles.maxWidth;
            },
            setMaxWidth: (value: LogoStylesLegacy['maxWidth']) => {
                _raw.styles.maxWidth().value.set(value);
            },
        }
    );
}

export type LogoForm = ReturnType<typeof createLogoForm>;
